import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { exactDecimalSchema, faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";
import { ApiError, describeApiFieldError } from "./http";

// The fault-run link shared by the studies that read a stored EOS-04 fault level
// (starter coordination, EOS-05a; breaker selectivity, EOS-05b): the selector's list
// of a revision's fault runs, and the fault level one chosen run holds. The backend
// mirror is app/services/fault_link.py.

const FAULT_RUNS_URL = "/api/v1/electrical/fault/runs";

/** The selector's own sentence for a study that names no fault run of the revision. */
export const CHOOSE_A_FAULT_RUN = "Choose a fault run of this project revision.";

// What the fault-run selector shows from the runs list: the list carries run
// summaries only, never a result field (CalculationRunListResponse).
export const faultRunChoiceSchema = z
  .object({
    id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
    design_check_status: z.string(),
    jurisdiction_profile: z.string(),
  })
  .strict();

const faultRunListSchema = z
  .object({
    items: z.array(calculationRunSummarySchema),
  })
  .strict();

// The fault level of one stored fault run, read from its frozen result for display
// only: the backend resolves the fault level again when the study is saved (A20 (d),
// A21 (a)). Only these seven fields are taken from the result snapshot; the rest is
// ignored. The peak ip is reported for a three-phase fault without converter current
// injection only, so an older or a converter-fed result may not carry it.
const faultLevelSnapshotSchema = z.object({
  fault_bus_code: z.string(),
  initial_symmetrical_short_circuit_current_ka: exactDecimalSchema,
  peak_short_circuit_current_ka: exactDecimalSchema.nullable().optional(),
  calculation_case: shortCircuitCaseSchema,
  fault_type: faultTypeSchema,
  nominal_voltage_v: exactDecimalSchema,
  status: z.string(),
});

const faultRunDetailSchema = calculationRunSummarySchema
  .extend({
    input_snapshot: z.record(z.string(), z.unknown()),
    result_snapshot: faultLevelSnapshotSchema,
    warnings_snapshot: z.array(z.unknown()),
    references_snapshot: z.record(z.string(), z.unknown()),
    // The fault run's evidence notes (A38 (d)); not used by a linked study, absent from an
    // answer of a server before A38.
    evidence_notes: z.array(z.string()).optional(),
  })
  .strict();

export const faultRunFaultLevelSchema = faultLevelSnapshotSchema
  .extend({
    run_id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
    // Always present here: null when the stored result holds no peak.
    peak_short_circuit_current_ka: exactDecimalSchema.nullable(),
  })
  .strict();

export type FaultRunChoice = z.infer<typeof faultRunChoiceSchema>;
export type FaultRunFaultLevel = z.infer<typeof faultRunFaultLevelSchema>;

/** The server's own words for a refusal, or one sentence per field of a validation error. */
export function formatApiError(data: unknown, status: number, subject: string): string {
  return describeApiFieldError(data, status, `${subject} failed`);
}

/** The caller's signal joined with a 30 s timeout. */
export function requestSignal(signal?: AbortSignal): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(30_000);
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
}

/**
 * The fault runs stored under one project revision, newest first, as the selector
 * lists them. The list carries run summaries only; the fault level of the chosen run
 * comes from getFaultRun.
 */
export async function listFaultRunsForRevision(
  projectRevisionId: string,
  signal?: AbortSignal,
): Promise<FaultRunChoice[]> {
  const query = new URLSearchParams({ project_revision_id: projectRevisionId });

  const response = await fetch(`${FAULT_RUNS_URL}?${query.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Fault run list"), response.status);
  }

  const parsed = faultRunListSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS fault runs API.");
  }

  return parsed.data.items.map((item) =>
    faultRunChoiceSchema.parse({
      id: item.id,
      calculation_key: item.calculation_key,
      revision_number: item.revision_number,
      design_check_status: item.design_check_status,
      jurisdiction_profile: item.jurisdiction_profile,
    }),
  );
}

/**
 * The fault level one stored fault run holds, for display beside the selector.
 *
 * Only the seven result fields the studies need are read; the backend reads the same
 * frozen result again when a study is saved, so nothing shown here is authoritative.
 * The peak ip is null when the stored result does not carry it.
 */
export async function getFaultRun(
  runId: string,
  signal?: AbortSignal,
): Promise<FaultRunFaultLevel> {
  const response = await fetch(`${FAULT_RUNS_URL}/${runId}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Fault run"), response.status);
  }

  const parsed = faultRunDetailSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS fault runs API.");
  }

  return faultRunFaultLevelSchema.parse({
    run_id: parsed.data.id,
    calculation_key: parsed.data.calculation_key,
    revision_number: parsed.data.revision_number,
    ...parsed.data.result_snapshot,
    peak_short_circuit_current_ka: parsed.data.result_snapshot.peak_short_circuit_current_ka ?? null,
  });
}
